import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminKCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';


const vitK_AI_Table = { // µg/day Phylloquinone (K1)
    '0-6m': 2.0, // AI
    '7-12m': 2.5, // AI
    '1-3y': 30,
    '4-8y': 55,
    '9-13y': 60,
    '14-18y': 75,
    '19+y_male': 120,
    '19+y_female': 90,
    'pregnant_lactating_14-18': 75, // Same as non-pregnant/lactating peers
    'pregnant_lactating_19+': 90,   // Same as non-pregnant/lactating peers
};

// Note: No official DRI for K2. Using prompt's suggestion.
const defaultK2Target = 100; // µg/day
const enhancedK2Target = 150; // µg/day for bone/cardio flags

// 5. Data tables to preload
const foodVitKValues = { // µg per serving (K1 or K2 specified)
    // K1 Sources
    kale_cooked_half_cup: { k1: 530 }, // Updated typical value based on USDA search ~1060/cup
    spinach_cooked_half_cup: { k1: 445 }, // Updated typical value based on USDA search ~890/cup
    broccoli_cooked_half_cup: { k1: 110 },
    canola_oil_tbsp: { k1: 10 }, // Updated from '20 per serving' assuming serving ~2 tbsp
    soybean_oil_tbsp: { k1: 25 },
    leafy_greens_generic_cup_raw: { k1: 100 }, // Generic estimate for mixed greens/lettuce

    // K2 Sources (MK-4, MK-7, MK-9 etc.) - specify type if known
    natto_1oz: { k2: 300, k2_type: 'MK-7' }, // Prompt: 1000/100g -> ~280-300/oz
    gouda_cheese_1oz: { k2: 20, k2_type: 'MK-8/9' }, // Prompt: 75/50g -> ~43/50g -> ~20-25/oz
    hard_cheese_generic_1oz: { k2: 15 }, // Generic lower estimate
    egg_yolk_one: { k2: 13, k2_type: 'MK-4' }, // Prompt: 25/2 yolks -> ~12.5/yolk
    chicken_thigh_cooked_75g: { k2: 13, k2_type: 'MK-4' },
    chicken_liver_cooked_3oz: { k2: 14, k2_type: 'MK-4' }, // Check source, can vary
    beef_liver_cooked_3oz: { k2: 5, k1: 5 }, // Contains both
};

// 5. Supplement Catalog Examples (for reference/suggestions)
const supplementExamples = [
    { name: "K2 MK-7 100 µg", type: 'k2', subtype: 'mk7', dose_mcg: 100 },
    { name: "K2 MK-4 1 mg (1000 µg)", type: 'k2', subtype: 'mk4', dose_mcg: 1000 }, // Therapeutic doses can be higher (45mg Japan) but start lower
    { name: "K1 100 µg", type: 'k1', subtype: 'k1', dose_mcg: 100 },
    { name: "D3/K2 Combo (5000 IU D3 + 100 µg K2 MK-7)", type: 'combo', subtype: 'mk7', dose_mcg: 100, vit_d_iu: 5000 },
];

// 5. Absorption Penalties
const absorptionPenalties = {
    low_fat_meal: 0.10, // 10% reduction
    malabsorption: 0.15, // 15% reduction
};

// 5. Safety Matrix Thresholds
const safetyThresholds = {
    k1_supplement_warn_mcg: 1000, // Warn if K1 supplement > 1 mg (1000 µg) without MD advice
    k2_plus_warfarin_warn_mcg: 400, // Warn if K2 supplement > 400 µg AND on Warfarin
    warfarin_consistency_threshold_mcg: 50, // Flag if change > 50 µg total K while on warfarin
};

// --- Helper Functions ---

// Get K1 AI key based on age/sex/status
const getK1AiKey = (ageNum, sex, isPregnant, isLactating) => {
    if (ageNum < 0.5) return '0-6m';
    if (ageNum < 1) return '7-12m';
    if (ageNum <= 3) return '1-3y';
    if (ageNum <= 8) return '4-8y';
    if (ageNum <= 13) return '9-13y';
    if (ageNum <= 18) {
        return (isPregnant || isLactating) ? 'pregnant_lactating_14-18' : '14-18y';
    }
    if (ageNum >= 19) {
        if (isPregnant || isLactating) return 'pregnant_lactating_19+';
        return sex === 'male' ? '19+y_male' : '19+y_female';
    }
    return '19+y_female'; // Default fallback
};


// --- Component ---
function VitaminKCalculator() { // <<< RENAMED Component
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // 'male', 'female'
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);

    // Cardio/Bone Flags
    const [cardioBoneFlags, setCardioBoneFlags] = useState({
        cad: false, // Coronary Artery Disease
        osteoporosis: false, // Osteopenia/Osteoporosis
        fractureHealing: false, // Currently healing a fracture
    });

    // Diet Diary (Weekly Servings)
    const [dietServings, setDietServings] = useState({
        leafyGreens: '', // Kale, Spinach etc. (High K1)
        broccoli: '', // Moderate K1
        soybeanCanolaOil: '', // Servings/Tbsp per week (K1)
        natto: '', // Servings per week (High K2-MK7)
        hardCheese: '', // Gouda, etc. (K2-MK9)
        eggYolk: '', // (K2-MK4)
        liver: '', // Chicken/Beef (K2-MK4)
    });

    // Supplement Use
    const [takesKSupplement, setTakesKSupplement] = useState(false);
    const [supplementKType, setSupplementKType] = useState(''); // 'k1', 'k2_mk4', 'k2_mk7', 'combo'
    const [supplementKDoseMcg, setSupplementKDoseMcg] = useState(''); // µg per dose
    const [supplementKFrequency, setSupplementKFrequency] = useState(''); // 'daily', 'weekly', etc.

    // Fat Intake & Absorption
    const [fatIntakeStatus, setFatIntakeStatus] = useState('normal'); // 'normal', 'low_fat'
    const [malabsorptionConditions, setMalabsorptionConditions] = useState(false); // Celiac, IBD, Bariatric

    // Medications
    const [medications, setMedications] = useState({
        warfarin: false,
        doacs: false, // DOACs generally don't interact, but good to note anticoagulation
        antibiotics: false, // Broad spectrum, recent/current use
        bileAcidSequestrants: false, // e.g., Cholestyramine
    });

    // Vitamin D Intake (for synergy note)
    const [vitaminDIU, setVitaminDIU] = useState(''); // IU/day

    // Optional Lab Inputs (Not used in core calc, but for context)
    const [labINR, setLabINR] = useState(''); // For Warfarin users
    const [labDpUcMGP, setLabDpUcMGP] = useState(''); // Advanced K status marker

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null);
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleCardioBoneFlagChange = (event) => {
        const { name, checked } = event.target;
        setCardioBoneFlags(prev => ({ ...prev, [name]: checked }));
    };
    const handleDietChange = (event) => {
        const { name, value } = event.target;
        // Ensure Natto servings are treated numerically or handle checkbox separately if preferred
        setDietServings(prev => ({ ...prev, [name]: value }));
    };
    const handleMedicationChange = (event) => {
        const { name, checked } = event.target;
        setMedications(prev => ({ ...prev, [name]: checked }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isFemale = sex === 'female';
    const isOnWarfarin = medications.warfarin;

    // Disable pregnancy/lactation based on sex and age
    useEffect(() => {
        if (sex === 'male' || ageNum < 12 || ageNum > 55) {
            setIsPregnant(false);
            setIsLactating(false);
        }
    }, [sex, ageNum]);

    // --- Calculation Logic ---
    const calculateVitaminKStatus = (e) => {
        e.preventDefault();
        setShowResults(false); // Reset results on new calculation
        setCalculationResult(null);

        let explanation = [];
        let safetyFlags = [];
        let suggestions = { food: [], supplement: [], lifestyle: [], general: [] };
        let targetK1_AI = 0;
        let targetK2_Personalized = defaultK2Target; // Start with default K2 target

        // Validate essential inputs
        if (isNaN(ageNum) || !sex) {
            alert("Please fill in required fields: Age and Sex.");
            return;
        }

        // --- 1. Target Setting ---
        const aiKey = getK1AiKey(ageNum, sex, isPregnant, isLactating);
        targetK1_AI = vitK_AI_Table[aiKey] || (sex === 'male' ? 120 : 90); // Fallback AI
        explanation.push(`Base K1 Target (AI for ${aiKey.replace('_', ' ')}): ${targetK1_AI} µg/day (total Vitamin K as phylloquinone).`);

        // Adjust K2 target based on flags
        if (cardioBoneFlags.cad || cardioBoneFlags.osteoporosis || cardioBoneFlags.fractureHealing) {
            targetK2_Personalized = enhancedK2Target;
            explanation.push(`Personalized K2 Target increased to ${targetK2_Personalized} µg/day due to selected Cardio/Bone health flags.`);
        } else {
            explanation.push(`Personalized K2 Target: ${targetK2_Personalized} µg/day (based on general observational data for bone/CV health).`);
        }

        // Warfarin overrides gap-filling goal with consistency goal
        if (isOnWarfarin) {
            explanation.push(`WARFARIN USE: The primary goal is maintaining CONSISTENT daily Vitamin K intake, not hitting a specific target gap. Avoid large fluctuations.`);
            suggestions.general.push(
                "WARFARIN USERS: Maintain a consistent Vitamin K intake from day to day. Report any significant dietary changes (like starting to eat lots of greens) or supplement changes to your prescribing doctor, as your INR may need adjustment. Do NOT start or stop K supplements without medical consultation."
            );
            // We still calculate the gap for context, but the main message is consistency.
        }

        // --- 2. Intake Tally (Average Daily µg) ---
        let k1_food = 0;
        let k2_food = 0;

        const weeklyServings = {
            leafyGreens: parseInt(dietServings.leafyGreens, 10) || 0,
            broccoli: parseInt(dietServings.broccoli, 10) || 0,
            soybeanCanolaOil: parseInt(dietServings.soybeanCanolaOil, 10) || 0, // Assume tbsp/week
            natto: parseInt(dietServings.natto, 10) || 0, // Assume 1oz servings/week
            hardCheese: parseInt(dietServings.hardCheese, 10) || 0, // Assume 1oz servings/week
            eggYolk: parseInt(dietServings.eggYolk, 10) || 0, // Assume # yolks/week
            liver: parseInt(dietServings.liver, 10) || 0, // Assume 3oz servings/week
        };

        // K1 Food Calc
        k1_food += (weeklyServings.leafyGreens * foodVitKValues.kale_cooked_half_cup.k1) / 7; // Example using Kale, could average or use generic
        k1_food += (weeklyServings.broccoli * foodVitKValues.broccoli_cooked_half_cup.k1) / 7;
        // Average K1 from oils - assumes 1 tbsp serving entered weekly
        k1_food += (weeklyServings.soybeanCanolaOil * (foodVitKValues.soybean_oil_tbsp.k1 + foodVitKValues.canola_oil_tbsp.k1) / 2) / 7;
        k1_food += (weeklyServings.liver * (foodVitKValues.beef_liver_cooked_3oz.k1 || 0)) / 7; // Add liver K1

        // K2 Food Calc
        k2_food += (weeklyServings.natto * foodVitKValues.natto_1oz.k2) / 7;
        k2_food += (weeklyServings.hardCheese * foodVitKValues.gouda_cheese_1oz.k2) / 7; // Example using Gouda
        k2_food += (weeklyServings.eggYolk * foodVitKValues.egg_yolk_one.k2) / 7;
        // Average K2 from liver types if 'liver' input used
        k2_food += (weeklyServings.liver * (foodVitKValues.chicken_liver_cooked_3oz.k2 + foodVitKValues.beef_liver_cooked_3oz.k2) / 2) / 7;


        explanation.push(`Estimated average dietary intake: K1 = ${k1_food.toFixed(0)} µg/day, K2 = ${k2_food.toFixed(0)} µg/day.`);

        let k1_supp = 0;
        let k2_supp = 0;
        const dose_mcg = parseInt(supplementKDoseMcg, 10);

        if (takesKSupplement && !isNaN(dose_mcg) && dose_mcg > 0 && supplementKFrequency && supplementKType) {
            let dailyFactor = 0;
            switch (supplementKFrequency) {
                case 'daily': dailyFactor = 1; break;
                case 'alternate_day': dailyFactor = 0.5; break;
                case '3x_week': dailyFactor = 3 / 7; break;
                case 'weekly': dailyFactor = 1 / 7; break;
                case 'monthly': dailyFactor = 1 / 30; break; // Less common for K
                default: dailyFactor = 0; break;
            }

            const avgDailySuppDose = dose_mcg * dailyFactor;

            if (supplementKType === 'k1') {
                k1_supp = avgDailySuppDose;
            } else if (supplementKType.startsWith('k2')) { // Catches k2_mk4, k2_mk7
                k2_supp = avgDailySuppDose;
            } else if (supplementKType === 'combo') {
                // Assume combo dose refers to K2 component unless specified otherwise
                k2_supp = avgDailySuppDose;
                // We'd ideally parse the combo product name/data for K1 if present
            }
            explanation.push(`Supplement contribution: ${dose_mcg} µg ${supplementKType.replace('_', ' ')} taken ${supplementKFrequency.replace('_', ' ')} ≈ ${avgDailySuppDose.toFixed(0)} µg/day average (K1: ${k1_supp.toFixed(0)}, K2: ${k2_supp.toFixed(0)}).`);
        }

        // --- 3. Absorption Adjustment ---
        let abs_factor = 1.0;
        let penaltiesApplied = [];
        if (fatIntakeStatus === 'low_fat') {
            abs_factor -= absorptionPenalties.low_fat_meal;
            penaltiesApplied.push('Low-fat diet');
        }
        if (malabsorptionConditions || medications.bileAcidSequestrants) {
            abs_factor -= absorptionPenalties.malabsorption; // Using same penalty for now
            penaltiesApplied.push('Malabsorption condition or bile acid sequestrant');
            safetyFlags.push({ type: 'info', text: 'Malabsorption condition or medication affecting fat absorption selected. Vitamin K absorption may be significantly reduced. Discuss status and needs with your doctor.' });
        }
        // Antibiotics affect gut synthesis of K2, not absorption of dietary K directly as much. Add a note instead of penalty.
        if (medications.antibiotics) {
            safetyFlags.push({ type: 'info', text: 'Broad-spectrum antibiotic use can reduce gut bacteria synthesis of Vitamin K2 (menaquinones). Consider K2-rich foods or discuss supplementation if use is long-term.' });
            suggestions.lifestyle.push("If on long-term broad-spectrum antibiotics, discuss potential impact on Vitamin K2 synthesis with your doctor. Include K2-rich foods like natto or aged cheeses if appropriate.");
        }


        abs_factor = Math.max(0, abs_factor); // Ensure factor doesn't go below 0
        if (penaltiesApplied.length > 0) {
            explanation.push(`Absorption factor applied: ${abs_factor.toFixed(2)}x due to (${penaltiesApplied.join('; ')}). Affects dietary and supplemental K.`);
        }

        const effective_k1 = (k1_food + k1_supp) * abs_factor;
        const effective_k2 = (k2_food + k2_supp) * abs_factor;
        const effective_total_k = effective_k1 + effective_k2;

        explanation.push(`==> Estimated TOTAL EFFECTIVE intake: K1 = ${effective_k1.toFixed(0)} µg/day, K2 = ${effective_k2.toFixed(0)} µg/day.`);

        // --- 4. Gap & Safety ---
        const gap_k1 = targetK1_AI - effective_k1;
        const gap_k2 = targetK2_Personalized - effective_k2;

        explanation.push(`Gap vs K1 Target (AI): ${gap_k1 > 0 ? `${gap_k1.toFixed(0)} µg/day deficit` : `Adequate / Surplus`}.`);
        explanation.push(`Gap vs K2 Target (Personalized): ${gap_k2 > 0 ? `${gap_k2.toFixed(0)} µg/day deficit` : `Adequate / Surplus`}.`);

        // Warfarin Consistency Check (Simplified: Flags if on Warfarin and taking supplements or has large gap)
        // A real check would compare vs previous known stable intake.
        if (isOnWarfarin && (k1_supp > 0 || k2_supp > 0 || Math.abs(gap_k1) > safetyThresholds.warfarin_consistency_threshold_mcg || Math.abs(gap_k2) > safetyThresholds.warfarin_consistency_threshold_mcg)) {
            safetyFlags.push({ type: 'warning', text: `WARFARIN CONSISTENCY ALERT: Your current estimated intake (Total K ~${effective_total_k.toFixed(0)} µg/day) or supplement use suggests potential for variability. Aim for stable daily intake. Consult prescriber BEFORE making changes to diet or supplements.` });
            // Override gap-based supplement suggestions if on Warfarin
            suggestions.supplement = ["Maintain current supplement routine unless advised otherwise by your doctor due to Warfarin."];
        } else if (gap_k2 >= 50 && !isOnWarfarin) { // Suggest supplement only if NOT on warfarin and K2 gap is significant
            suggestions.supplement.push(`You are estimated to be ${gap_k2.toFixed(0)} µg short of your daily K2 target. Consider adding K2-rich foods.`);
            suggestions.supplement.push(`If dietary changes are difficult, a supplement like ${supplementExamples[0].name} could help bridge the gap. Discuss with your doctor, especially if you have health conditions.`);
            // Placeholder for affiliate link/product suggestion:
            // suggestions.supplement.push(`Example: [Link to ${supplementExamples[0].name}]`);
        } else if (gap_k1 > 50 && !isOnWarfarin) { // K1 gap suggestion (less common focus)
            suggestions.supplement.push(`You are estimated to be ${gap_k1.toFixed(0)} µg short of your daily K1 target (AI). Increase intake of leafy green vegetables.`);
        }

        // General Food Suggestions
        suggestions.food.push(`Good K1 sources: Leafy greens (kale, spinach - aim for ~1 cup cooked/day if needed), broccoli.`);
        suggestions.food.push(`Good K2 sources: Natto (very high in MK-7, 1oz ≈ 300µg), hard cheeses (Gouda, Brie ≈ 20µg/oz), egg yolks (≈ 13µg/yolk), chicken thigh/liver.`);
        if (k2_food < 50) {
            suggestions.food.push("Your dietary K2 intake appears low. Try incorporating more fermented foods (natto, certain cheeses) or egg yolks.");
        }
        if (k1_food < targetK1_AI / 2) {
            suggestions.food.push("Your dietary K1 intake appears low. Ensure regular consumption of green leafy vegetables.");
        }


        // Safety Flags
        if (k1_supp > safetyThresholds.k1_supplement_warn_mcg && !isOnWarfarin) {
            safetyFlags.push({ type: 'warning', text: `High K1 Supplement Dose: You are taking over ${safetyThresholds.k1_supplement_warn_mcg} µg (${safetyThresholds.k1_supplement_warn_mcg / 1000} mg) of K1 from supplements. While generally safe, doses above 1 mg are not typically needed unless medically advised. Confirm rationale with your doctor.` });
        }
        if (k2_supp > safetyThresholds.k2_plus_warfarin_warn_mcg && isOnWarfarin) {
            safetyFlags.push({ type: 'severe', text: `High K2 Supplement Dose with Warfarin: Taking over ${safetyThresholds.k2_plus_warfarin_warn_mcg} µg of K2 while on Warfarin requires close medical supervision and INR monitoring. CONSULT YOUR PRESCRIBER IMMEDIATELY.` });
        }
        if (medications.doacs && (k1_supp > 0 || k2_supp > 0)) {
            safetyFlags.push({ type: 'info', text: `DOAC Use: While direct interactions are less common than with Warfarin, it's prudent to inform your doctor about any Vitamin K supplement use when taking any anticoagulant.` });
        }

        // Vitamin D Synergy Note
        const vitDValue = parseInt(vitaminDIU, 10);
        if (!isNaN(vitDValue) && vitDValue >= 1000 && effective_k2 > 50) {
            suggestions.general.push(`VITAMIN D/K2 SYNERGY: You report taking Vitamin D (${vitDValue} IU). Adequate Vitamin K2 helps ensure calcium activated by Vitamin D goes to bones and not arteries. Your K2 intake seems supportive (>50 µg/day).`);
            // Link to article placeholder
            suggestions.general.push("Learn more about D3/K2 synergy [link placeholder].");
        } else if (!isNaN(vitDValue) && vitDValue >= 1000 && effective_k2 <= 50) {
            suggestions.general.push(`VITAMIN D/K2 SYNERGY: You report taking Vitamin D (${vitDValue} IU). Consider increasing Vitamin K2 intake (target ~${targetK2_Personalized} µg/day) to support proper calcium utilization promoted by Vitamin D.`);
            safetyFlags.push({ type: 'info', text: 'Vitamin D intake noted, but K2 intake appears low. Discuss optimizing K2 for potential synergy benefits with your doctor.' });
        }

        // Optional Lab Context
        if (labINR) {
            explanation.push(`Reported INR: ${labINR}. (Ensure this is stable if on Warfarin).`);
        }
        if (labDpUcMGP) {
            explanation.push(`Reported dp-ucMGP: ${labDpUcMGP}. (Lower values suggest better K status; interpretation requires clinical context).`);
            suggestions.general.push(`Your dp-ucMGP level (${labDpUcMGP}) provides an advanced indication of Vitamin K status related to vascular health. Discuss interpretation with your doctor.`);
        }

        // Set results state
        setCalculationResult({
            targetK1: targetK1_AI.toFixed(0),
            targetK2: targetK2_Personalized.toFixed(0),
            foodK1: k1_food.toFixed(0),
            foodK2: k2_food.toFixed(0),
            suppK1: k1_supp.toFixed(0),
            suppK2: k2_supp.toFixed(0),
            absorptionFactor: abs_factor.toFixed(2),
            effectiveK1: effective_k1.toFixed(0),
            effectiveK2: effective_k2.toFixed(0),
            effectiveTotalK: effective_total_k.toFixed(0),
            gapK1: gap_k1.toFixed(0),
            gapK2: gap_k2.toFixed(0),
            isOnWarfarin: isOnWarfarin,
            explanation: explanation.join('\n'),
            suggestions: suggestions,
            safetyFlags: safetyFlags,
        });
        setShowResults(true);
    };


    // --- JSX Structure ---
    return (
        <div className="vitaminKCalculator"> {/* <<< UPDATED CLASS */}
            <h1>
                <span>
                    <span role="img" aria-label="vitamin k calculator">
                        🥬
                    </span>{' '}
                    Vitamin K Calculator
                </span>
                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This calculator ESTIMATES your Vitamin K intake (K1 & K2) based on general data and your inputs. It is NOT a substitute for professional medical advice. Vitamin K needs, absorption, and interactions (especially with Warfarin) are complex. Consult your healthcare provider regarding your Vitamin K status, particularly if you take anticoagulants (like Warfarin), have health conditions (malabsorption, bone disease, heart disease), are pregnant/lactating, or are considering supplements. Do not make changes to diet or supplements, especially if on Warfarin, without medical supervision.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateVitaminKStatus}> {/* <<< UPDATED HANDLER */}
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <h3>👤 Demographics</h3>
                    <label>
                        Age (Years):*
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 55" />
                    </label>
                    <label>
                        Sex Assigned at Birth:*
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {isFemale && (
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Pregnant?
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Lactating?
                            </label>
                        </>
                    )}
                </div>

                {/* Cardio/Bone Flags */}
                <div className="form-section">
                    <h3>❤️🦴 Cardiovascular & Bone Health Flags</h3>
                    <fieldset>
                        <legend>Check any that apply (may increase K2 target):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cad" checked={cardioBoneFlags.cad} onChange={handleCardioBoneFlagChange} />
                            History of Coronary Artery Disease (CAD) or known Calcification?
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="osteoporosis" checked={cardioBoneFlags.osteoporosis} onChange={handleCardioBoneFlagChange} />
                            Diagnosed Osteopenia or Osteoporosis?
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="fractureHealing" checked={cardioBoneFlags.fractureHealing} onChange={handleCardioBoneFlagChange} />
                            Currently healing a bone fracture?
                        </label>
                    </fieldset>
                </div>


                {/* Diet */}
                <div className="form-section">
                    <h3>🍽️ Dietary Intake (Average Weekly Servings)</h3>
                    <small>Enter typical number of servings PER WEEK.</small>
                    <div className="diet-grid k-diet-grid"> {/* Added k-diet-grid class if specific styling needed */}
                        {/* K1 Rich Foods */}
                        <label>
                            Leafy Greens (Kale, Spinach):
                            <input type="number" name="leafyGreens" value={dietServings.leafyGreens} onChange={handleDietChange} min="0" placeholder="e.g., 7" />
                            <small>(~1/2 cup cooked per serving)</small>
                        </label>
                        <label>
                            Broccoli:
                            <input type="number" name="broccoli" value={dietServings.broccoli} onChange={handleDietChange} min="0" placeholder="e.g., 3" />
                            <small>(~1/2 cup cooked per serving)</small>
                        </label>
                        <label>
                            Soybean/Canola Oil (Tbsp):
                            <input type="number" name="soybeanCanolaOil" value={dietServings.soybeanCanolaOil} onChange={handleDietChange} min="0" placeholder="e.g., 10" />
                            <small>(Total Tbsps used weekly)</small>
                        </label>

                        {/* K2 Rich Foods */}
                        <label>
                            Natto:
                            <input type="number" name="natto" value={dietServings.natto} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
                            <small>(~1 oz / 30g per serving)</small>
                        </label>
                        <label>
                            Hard Cheese (Gouda, Brie):
                            <input type="number" name="hardCheese" value={dietServings.hardCheese} onChange={handleDietChange} min="0" placeholder="e.g., 4" />
                            <small>(~1 oz / 30g per serving)</small>
                        </label>
                        <label>
                            Egg Yolks (Number):
                            <input type="number" name="eggYolk" value={dietServings.eggYolk} onChange={handleDietChange} min="0" placeholder="e.g., 5" />
                            <small>(Number of yolks per week)</small>
                        </label>
                        <label>
                            Liver (Chicken, Beef):
                            <input type="number" name="liver" value={dietServings.liver} onChange={handleDietChange} min="0" placeholder="e.g., 0" />
                            <small>(~3 oz / 85g cooked per serving)</small>
                        </label>
                    </div>
                </div>

                {/* Supplements */}
                <div className="form-section">
                    <h3>💊 Vitamin K Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesKSupplement} onChange={(e) => setTakesKSupplement(e.target.checked)} />
                        Do you currently take a Vitamin K supplement?
                    </label>
                    {takesKSupplement && (
                        <div className='supplement-details'>
                            <label>
                                Type of Vitamin K:*
                                <select value={supplementKType} onChange={(e) => setSupplementKType(e.target.value)} required={takesKSupplement}>
                                    <option value="">-- Select --</option>
                                    <option value="k1">K1 (Phylloquinone)</option>
                                    <option value="k2_mk4">K2 (MK-4)</option>
                                    <option value="k2_mk7">K2 (MK-7)</option>
                                    <option value="combo">Combo D3/K2 (Specify K dose below)</option>
                                    <option value="unknown">Unknown Type</option>
                                </select>
                            </label>
                            <label>
                                Dose per intake (µg - micrograms):*
                                <input type="number" value={supplementKDoseMcg} onChange={(e) => setSupplementKDoseMcg(e.target.value)} required={takesKSupplement} min="1" placeholder="e.g., 100" />
                                <small>Note: 1 mg = 1000 µg</small>
                            </label>
                            <label>
                                Frequency:*
                                <select value={supplementKFrequency} onChange={(e) => setSupplementKFrequency(e.target.value)} required={takesKSupplement}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="alternate_day">Alternate Days</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="other">Other / Irregular</option>
                                </select>
                            </label>
                            {/* Suggestion based on catalog */}
                            <small>Common K2 forms: MK-7 (longer half-life, from natto), MK-4 (shorter half-life, animal sources). K1 primarily supports clotting.</small>
                        </div>
                    )}
                </div>

                {/* Fat Intake & Absorption */}
                <div className="form-section">
                    <h3>🧈 Fat Intake & Absorption Factors</h3>
                    <label>
                        Typical Fat Intake with Meals:
                        <select value={fatIntakeStatus} onChange={(e) => setFatIntakeStatus(e.target.value)}>
                            <option value="normal">Normal / Adequate Fat</option>
                            <option value="low_fat">Consistently Low-Fat Diet</option>
                        </select>
                        <small>Vitamin K is fat-soluble; absorption is better with some dietary fat.</small>
                    </label>
                    <label className="checkbox-label indented">
                        <input type="checkbox" checked={malabsorptionConditions} onChange={(e) => setMalabsorptionConditions(e.target.checked)} />
                        Do you have a condition affecting fat absorption? (e.g., Celiac, IBD, Crohn's, Ulcerative Colitis, Bariatric Surgery History)
                    </label>
                </div>


                {/* Medications */}
                <div className="form-section">
                    <h3>💊 Medications</h3>
                    <fieldset>
                        <legend>Check any you take regularly:</legend>
                        <label className="checkbox-label indented alert-warfarin">
                            <input type="checkbox" name="warfarin" checked={medications.warfarin} onChange={handleMedicationChange} />
                            <strong>Warfarin (Coumadin, Jantoven)?</strong>
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="doacs" checked={medications.doacs} onChange={handleMedicationChange} />
                            Other Anticoagulant (DOAC)? (e.g., Eliquis, Xarelto, Pradaxa)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="antibiotics" checked={medications.antibiotics} onChange={handleMedicationChange} />
                            Recent or Current Broad-Spectrum Antibiotics? (Impairs K2 synthesis)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="bileAcidSequestrants" checked={medications.bileAcidSequestrants} onChange={handleMedicationChange} />
                            Bile Acid Sequestrants? (e.g., Cholestyramine - impairs fat absorption)
                        </label>
                    </fieldset>
                </div>

                {/* Vitamin D Intake */}
                <div className="form-section">
                    <h3>☀️ Vitamin D Intake (for K2 Synergy Note)</h3>
                    <label>
                        Estimated Average Daily Vitamin D Intake (IU):
                        <input type="number" value={vitaminDIU} onChange={(e) => setVitaminDIU(e.target.value)} min="0" placeholder="e.g., 2000 (from sun, food, supps)" />
                        <small>Enter your total estimated D3 intake if known.</small>
                    </label>
                </div>


                {/* Optional Lab Inputs */}
                <div className="form-section">
                    <h3>🩸 Optional Lab Values (For Context)</h3>
                    <label>
                        Most Recent INR (if on Warfarin):
                        <input type="text" value={labINR} onChange={(e) => setLabINR(e.target.value)} placeholder="e.g., 2.5" />
                    </label>
                    <label>
                        dp-ucMGP (Desphospho-uncarboxylated MGP - if known):
                        <input type="text" value={labDpUcMGP} onChange={(e) => setLabDpUcMGP(e.target.value)} placeholder="e.g., 450 pmol/L" />
                        <small>Advanced marker of vascular K status (research/specialty).</small>
                    </label>
                </div>


                <button type="submit">Calculate Vitamin K Status</button> {/* <<< UPDATED BUTTON TEXT */}
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Vitamin K Status</h2>

                    {/* Warfarin Banner */}
                    {calculationResult.isOnWarfarin && (
                        <div className="warfarin-banner alert-severe">
                            <h4>🚨 WARFARIN USER ALERT 🚨</h4>
                            <p>You indicated you are taking Warfarin. <strong>CONSISTENCY is KEY.</strong> Do NOT change your Vitamin K intake (diet or supplements) without consulting your prescriber. Abrupt changes can dangerously affect your INR. The goal is stable intake, not hitting a specific target number from this calculator.</p>
                        </div>
                    )}

                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Important Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Dual Gauge Placeholder */}
                    <div className="result-gauge-placeholder dual-gauge">
                        <div className="gauge-pair">
                            <p>K1 vs AI Target</p>
                            {/* Simple Bar Representation */}
                            <div className="gauge-bar" style={{ '--effective-value': calculationResult.effectiveK1, '--target-value': calculationResult.targetK1, '--max-value': Math.max(200, calculationResult.targetK1 * 1.5) }}> {/* Adjust max-value as needed */}
                                <div className="gauge-fill"></div>
                                <div className="gauge-target-line"></div>
                            </div>
                            <span className="gauge-label">Effective: {calculationResult.effectiveK1} µg / Target: {calculationResult.targetK1} µg</span>
                            <span className={`gauge-status status-${calculationResult.gapK1 > 0 ? 'low' : 'adequate'}`}>
                                {calculationResult.gapK1 > 0 ? `(~${calculationResult.gapK1} µg deficit)` : 'Adequate'}
                            </span>
                        </div>
                        <div className="gauge-pair">
                            <p>K2 vs Personalized Target</p>
                            <div className="gauge-bar" style={{ '--effective-value': calculationResult.effectiveK2, '--target-value': calculationResult.targetK2, '--max-value': Math.max(200, calculationResult.targetK2 * 1.5) }}>
                                <div className="gauge-fill"></div>
                                <div className="gauge-target-line"></div>
                            </div>
                            <span className="gauge-label">Effective: {calculationResult.effectiveK2} µg / Target: {calculationResult.targetK2} µg</span>
                            <span className={`gauge-status status-${calculationResult.gapK2 > 0 ? 'low' : 'adequate'}`}>
                                {calculationResult.gapK2 > 0 ? `(~${calculationResult.gapK2} µg deficit)` : 'Adequate'}
                            </span>
                        </div>
                    </div>

                    <div className="results-grid">
                        {/* Update result items for Vitamin K */}
                        <div className="result-item">
                            <span className="result-label">K1 Target (AI):</span>
                            <span className="result-value">{calculationResult.targetK1} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">K2 Target (Personalized):</span>
                            <span className="result-value">{calculationResult.targetK2} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Dietary K1:</span>
                            <span className="result-value">{calculationResult.foodK1} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Dietary K2:</span>
                            <span className="result-value">{calculationResult.foodK2} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Avg. Supplement K1:</span>
                            <span className="result-value">{calculationResult.suppK1} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Avg. Supplement K2:</span>
                            <span className="result-value">{calculationResult.suppK2} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Absorption Factor:</span>
                            <span className="result-value">{calculationResult.absorptionFactor}x</span>
                            <small className='result-note'>(Due to fat intake/conditions)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Effective K1 Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveK1} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Effective K2 Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveK2} µg/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Total Effective K:</span>
                            <span className="result-value">{calculationResult.effectiveTotalK} µg/day</span>
                            <small className='result-note'>(Used for Warfarin consistency context)</small>
                        </div>
                    </div>

                    <h3>💡 Guidance & Suggestions</h3>
                    {/* Gap Narrative */}
                    <p className="gap-statement">
                        {calculationResult.gapK1 > 0 && `You appear to be about ${calculationResult.gapK1} µg short of the AI for K1 (phylloquinone), primarily involved in clotting. `}
                        {calculationResult.gapK2 > 0 && `You appear to be about ${calculationResult.gapK2} µg short of your personalized K2 target (menaquinones), important for bone and vascular health.`}
                        {(calculationResult.gapK1 <= 0 && calculationResult.gapK2 <= 0) && `Your estimated intake meets or exceeds both your K1 AI and personalized K2 targets.`}
                        {calculationResult.isOnWarfarin && <strong> Remember: For Warfarin users, consistency is more important than hitting these exact targets.</strong>}
                    </p>

                    {/* Suggestions Sections */}
                    {calculationResult.suggestions.food.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🍽️ Food Suggestions:</h4>
                            <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.supplement.length > 0 && !calculationResult.isOnWarfarin && ( // Only show supp suggestions if not on Warfarin
                        <div className="suggestions-box">
                            <h4>💊 Supplement Considerations (Discuss with Doctor):</h4>
                            <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
                            {/* Placeholder for affiliate links */}
                            {/* <p><small><i>Example supplement links could appear here.</i></small></p> */}
                        </div>
                    )}
                    {calculationResult.suggestions.lifestyle.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🌿 Lifestyle Factors:</h4>
                            <ul>{calculationResult.suggestions.lifestyle.map((s, i) => <li key={`life-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.general.length > 0 && (
                        <div className="suggestions-box">
                            <h4>⭐ General Notes:</h4>
                            <ul>{calculationResult.suggestions.general.map((s, i) => <li key={`gen-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}


                    <h3>📝 Calculation Explanation</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <p className="important-note">
                        <strong>Reminder:</strong> This is an educational tool. Individual needs vary. Consult your healthcare provider to determine the appropriate Vitamin K intake for you, especially if using anticoagulants or managing health conditions.
                    </p>
                    <a href="#vitk-education" className="scroll-link">Learn more about Vitamin K below</a> {/* <<< UPDATED LINK HASH */}
                </div>
            )}

            {/* --- Educational Content (Vitamin K specific) --- */}
            <div id="vitk-education" className="educational-section"> {/* <<< UPDATED ID */}
                <h2>Understanding Vitamin K</h2> {/* <<< UPDATED TITLE */}

                {/* Content Block 1: Fundamentals */}
                <div className="content-block">
                    <h3>1. Vitamin K Fundamentals</h3>
                    <dl>
                        <dt>Forms:</dt>
                        <dd>
                            <ul>
                                <li><strong>K1 (Phylloquinone):</strong> Found primarily in green leafy vegetables (kale, spinach, broccoli) and some plant oils (soybean, canola). Its main role is activating clotting factors in the liver. This is the form newborns receive via injection.</li>
                                <li><strong>K2 (Menaquinones):</strong> A family of related compounds (MK-4 to MK-13) found in animal products (liver, egg yolks, butter, cheese) and fermented foods (especially natto - rich in MK-7). Gut bacteria also produce some K2. K2 is crucial for activating proteins involved in bone health (osteocalcin) and preventing arterial calcification (matrix Gla protein - MGP). MK-4 and MK-7 are the most studied forms.</li>
                            </ul>
                        </dd>

                        <dt>Physiological Roles:</dt>
                        <dd>
                            <ul>
                                <li><strong>Blood Clotting:</strong> Essential for the γ-carboxylation (activation) of several clotting factors (II, VII, IX, X) and anticoagulant proteins (C, S, Z) in the liver. This is primarily K1's role.</li>
                                <li><strong>Bone Health:</strong> K2 activates osteocalcin, a protein needed to bind calcium into the bone matrix, improving bone quality and potentially reducing fracture risk.</li>
                                <li><strong>Cardiovascular Health:</strong> K2 activates Matrix Gla Protein (MGP), which helps inhibit calcium deposition (calcification) in artery walls and soft tissues. Low K status is linked to increased vascular calcification.</li>
                                <li><strong>Emerging Roles:</strong> Research suggests potential roles in insulin sensitivity, brain health/cognition, and anti-inflammatory processes, but more data is needed.</li>
                            </ul>
                        </dd>

                        <dt>Absorption:</dt>
                        <dd>Like other fat-soluble vitamins, Vitamin K requires dietary fat and bile acids for absorption in the small intestine. Conditions impairing fat absorption (e.g., cholestatic liver disease, celiac disease, inflammatory bowel disease, cystic fibrosis, bariatric surgery) can lead to deficiency.</dd>

                        <dt>Drug Interactions:</dt>
                        <dd>
                            <ul>
                                <li><strong>Warfarin (Coumadin):</strong> This anticoagulant works by directly antagonizing Vitamin K's action in the liver (inhibiting the VKOR enzyme needed to recycle K). Consistent Vitamin K intake is CRITICAL for stable anticoagulation (INR). Large fluctuations in K intake require dose adjustments.</li>
                                <li><strong>Antibiotics:</strong> Broad-spectrum antibiotics can kill gut bacteria that produce K2, potentially lowering K2 status, especially with prolonged use and poor dietary intake.</li>
                                <li><strong>Bile Acid Sequestrants:</strong> (e.g., Cholestyramine) Can reduce absorption of all fat-soluble vitamins, including K.</li>
                                <li><strong>Orlistat:</strong> Weight loss drug that inhibits fat absorption can also reduce Vitamin K absorption.</li>
                                <li><strong>DOACs:</strong> Newer anticoagulants like Eliquis, Xarelto, Pradaxa do NOT directly interact with Vitamin K metabolism in the same way as Warfarin.</li>
                            </ul>
                        </dd>

                        <dt>Safety & UL:</dt>
                        <dd>No Tolerable Upper Intake Level (UL) has been established for Vitamin K from food or supplements. Both K1 and K2 appear non-toxic at levels typically consumed from food and even with supplements up to 1 mg (1000 µg) K1 or several hundred µg of K2 daily for most people. However, high-dose K1 supplements (&gt;1 mg) are generally unnecessary unless medically indicated. Extreme doses of MK-4 (e.g., 45 mg used therapeutically in Japan for osteoporosis) require medical supervision. The primary safety concern is interaction with Warfarin.</dd>

                    </dl>
                </div>

                {/* Content Block 2: RDAs (AI) */}
                <div className="content-block">
                    <h3>2. Reference Intakes (Adequate Intake - AI)</h3>
                    <p>AI is set based on typical phylloquinone (K1) intakes in healthy populations. Assumes total Vitamin K.</p>
                    <table>
                        <thead>
                            <tr><th>Life Stage Group</th><th>AI (µg/day)</th></tr>
                        </thead>
                        <tbody>
                            {/* Re-populate from vitK_AI_Table */}
                            <tr><td>Infants 0–6 months</td><td>2.0</td></tr>
                            <tr><td>Infants 7–12 months</td><td>2.5</td></tr>
                            <tr><td>Children 1–3 years</td><td>30</td></tr>
                            <tr><td>Children 4–8 years</td><td>55</td></tr>
                            <tr><td>Children 9–13 years</td><td>60</td></tr>
                            <tr><td>Teens 14–18 years</td><td>75</td></tr>
                            <tr><td>Adult Men 19+ years</td><td>120</td></tr>
                            <tr><td>Adult Women 19+ years</td><td>90</td></tr>
                            <tr><td>Pregnancy / Lactation (14-18y)</td><td>75</td></tr>
                            <tr><td>Pregnancy / Lactation (19+y)</td><td>90</td></tr>
                        </tbody>
                    </table>
                    <small><strong>Note on K2:</strong> There is no official DRI (RDA or AI) specifically for Vitamin K2. Observational studies suggest potential bone and cardiovascular benefits with intakes around 100–200 µg/day, primarily from MK-7 and MK-4, but optimal levels are still debated.</small>
                </div>

                {/* Content Block 3: High Risk Groups */}
                <div className="content-block">
                    <h3>3. Higher-Risk or Higher-Need Groups</h3>
                    <ul>
                        <li><strong>Warfarin Users:</strong> Need CONSISTENT, not necessarily low, Vitamin K intake. Stability is paramount. Medical supervision needed for any changes. Some research explores low-dose K supplementation *with* Warfarin to stabilize INR (MD supervision ONLY).</li>
                        <li><strong>Individuals with Coronary Artery Calcification (CAC):</strong> Studies suggest suboptimal K2 status is common and linked to higher CAC scores. Higher K2 intake may be beneficial.</li>
                        <li><strong>Postmenopausal Women / Osteoporosis:</strong> K2 plays a role in bone metabolism (activating osteocalcin). Some studies show K2 (especially MK-4 at high doses, or MK-7) improves bone mineral density markers and may reduce fracture risk.</li>
                        <li><strong>Fat Malabsorption Conditions:</strong> Celiac disease, Crohn's disease, ulcerative colitis, cystic fibrosis, cholestasis, history of bariatric surgery significantly impair K absorption.</li>
                        <li><strong>Broad-Spectrum Antibiotic Users (Long-term):</strong> Reduced gut synthesis of K2.</li>
                        <li><strong>Liver Disease (Severe):</strong> Impaired synthesis of clotting factors, though K supplementation effectiveness varies.</li>
                        <li><strong>Newborns:</strong> Routinely receive a K1 injection at birth to prevent Vitamin K Deficiency Bleeding (VKDB), as they have low stores and limited gut flora. (This calculator is not intended for newborns).</li>
                    </ul>
                </div>


                {/* --- Sources Section (Vitamin K specific) --- */}
                <div className="content-block">
                    <h3>Sources</h3>
                    <p className="sources"> {/* Ensure .sources class has appropriate styling */}
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin K Fact Sheet for Health Professionals*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/ <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin K Fact Sheet for Consumers*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminK-Consumer/ <br />
                        Institute of Medicine (US) Panel on Micronutrients. (2001). *Dietary Reference Intakes for Vitamin A, Vitamin K, Arsenic, Boron, Chromium, Copper, Iodine, Iron, Manganese, Molybdenum, Nickel, Silicon, Vanadium, and Zinc*. National Academies Press. https://doi.org/10.17226/10026 <br />
                        DiNicolantonio, J. J., Bhutani, J., & O'Keefe, J. H. (2015). The health benefits of vitamin K. *Open Heart*, *2*(1), e000300. https://doi.org/10.1136/openhrt-2015-000300 (Review - PMC) <br />
                        Shea, M. K., & Booth, S. L. (2016). Update on the role of vitamin K in skeletal health. *Nutrition Reviews*, *74*(6), 390–397. https://doi.org/10.1093/nutrit/nuw014 (Bone Health - PMC) <br />
                        Schwalfenberg, G. K. (2017). Vitamins K1 and K2: The Emerging Group of Vitamins Required for Human Health. *Journal of Nutrition and Metabolism*, *2017*, 6254836. https://doi.org/10.1155/2017/6254836 (General Review - PMC) <br />
                        Fusaro, M., et al. (2017). Vitamin K and bone. *Clinical Cases in Mineral and Bone Metabolism*, *14*(2), 200–206. https://doi.org/10.11138/ccmbm/2017.14.1.200 (Bone - PMC) <br />
                        Vermeer, C. (2012). Vitamin K: the effect on health beyond coagulation - an overview. *Food & Nutrition Research*, *56*. https://doi.org/10.3402/fnr.v56i0.5329 (Overview - PMC) <br />
                        Sato, T., Schurgers, L. J., & Uenishi, K. (2012). Comparison of menaquinone-4 and menaquinone-7 bioavailability in healthy women. *Nutrition Journal*, *11*, 93. https://doi.org/10.1186/1475-2891-11-93 (Bioavailability - PMC) <br />
                        Beulens, J. W., et al. (2013). The role of menaquinones (vitamin K₂) in human health. *British Journal of Nutrition*, *110*(8), 1357-1368. (Role of K2 - PubMed link often available via PMC) <br />
                        USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from https://fdc.nal.usda.gov/ (For food values) <br />
                        *Replace [April-14-2025] with the actual date you accessed the links.*
                    </p>
                </div>
                {/* --- END OF SOURCES SECTION --- */}

            </div> {/* End educational-section */}
        </div> // End vitaminKCalculator
    );
}

export default VitaminKCalculator;